import * as React from "react";
import * as ReactDOM from "react-dom";
import { FC, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { Point } from "ol/geom";
import { Control } from "ol/control";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Feature } from "ol";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { config } from "../config";
import { coordinatesConverter, EpsgCode } from "@geo/js";
import { unByKey } from "ol/Observable";
import { EventsKey } from "ol/events";

enum LocationState {
    Inactive = 0,
    Waiting = 1,
    Watching = 2,
    Tracking = 3
}

export interface IPosition {
    coords: {
        latitude: number;
        longitude: number;
    };
}

const centerGraphicStyle = new Style({
    image: new CircleStyle({
        radius: 6,
        fill: new Fill({
            color: "#fff"
        }),
        stroke: new Stroke({
            color: "#00aeea",
            width: 4
        })
    })
});

const outlineGraphicStyle = new Style({
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "#00aeea4d",
            width: 4
        })
    })
});
export const GeoLocation: FC<{ this: Control }> = (props) => {
    const [locationState, setLocationState] = useState(LocationState.Inactive);
    const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
    const [isMapMoving, setIsMapMoving] = useState(false);
    const gpsWatcher = useRef<number>();
    const interactingWatcher = useRef<any>();
    const stationaryWatcher = useRef<EventsKey>();
    const centerGraphic = useRef<Feature>();
    const outlineGraphic = useRef<Feature>();
    const pointLayer = useRef<VectorLayer<VectorSource>>();

    const map = props.this.getMap();

    const geolocationOptions = config
        ? config.geoLocation.geolocationOptions
        : {
              maximumAge: 0,
              timeout: 15000,
              enableHighAccuracy: true
          };

    useEffect(() => {
        if (locationState === LocationState.Waiting) {
            startLocate();
        } else if (locationState === LocationState.Tracking) {
            startTracking();
        } else if (locationState === LocationState.Inactive) {
            stopLocate();
        }
    }, [locationState]);

    useEffect(() => {
        if (position.latitude === 0 && position.longitude === 0) {
            return;
        }

        placeMarker();
        if (locationState === LocationState.Waiting && !isMapMoving) {
            moveToPoint();
            setLocationState(LocationState.Tracking);
        } else if (locationState === LocationState.Tracking && !isMapMoving) {
            //@ts-ignore
            moveToPoint(map.getView().getZoom());
        }
    }, [position]);

    const moveToPoint = async (zoom?) => {
        setIsMapMoving(true);
        await mapGoTo(zoom);
        setIsMapMoving(false);
    };

    const onSuccess = (position: IPosition) => {
        const point = coordinatesConverter.convert(
            [position.coords.longitude, position.coords.latitude],
            EpsgCode.Wgs84,
            EpsgCode.Lks92
        );
        setPosition({ latitude: point[1], longitude: point[0] });
    };

    const onError = () => {
        setLocationState(LocationState.Inactive);
    };

    const startLocate = () => {
        gpsWatcher.current = navigator.geolocation.watchPosition(onSuccess, onError, geolocationOptions);
    };

    const stopLocate = () => {
        gpsWatcher.current && navigator.geolocation.clearWatch(gpsWatcher.current);

        stopTracking();

        if (pointLayer.current) {
            pointLayer.current.getSource().clear();
            //@ts-ignore
            map.removeLayer(pointLayer.current);
            pointLayer.current = null;
            centerGraphic.current = null;
            outlineGraphic.current = null;
        }
    };

    const startTracking = () => {
        //@ts-ignore
        const view = map.getView();

        if (!interactingWatcher.current) {
            interactingWatcher.current = setInterval(() => {
                if (view.getInteracting() && locationState != LocationState.Watching) {
                    setLocationState(LocationState.Watching);
                }
            }, 150);
        }
        if (!stationaryWatcher.current) {
            //@ts-ignore
            stationaryWatcher.current = map.on("moveend", refreshPosition);
        }

        if (!view.getInteracting() && !view.getAnimating()) {
            refreshPosition();
        }

        document.addEventListener("stopTracking", () => {
            stopTracking();
            setLocationState(LocationState.Watching);
        });
    };

    const stopTracking = () => {
        if (stationaryWatcher.current) {
            unByKey(stationaryWatcher.current);
            stationaryWatcher.current = null;
        }
        if (interactingWatcher.current) {
            clearInterval(interactingWatcher.current);
            interactingWatcher.current = null;
        }
        document.removeEventListener("stopTracking", stopTracking);
    };

    const refreshPosition = () => {
        if (locationState === LocationState.Tracking) {
            setPosition({
                latitude: position.latitude,
                longitude: position.longitude
            });
        }
    };

    const placeMarker = () => {
        const centerPoint = new Point([position.latitude, position.longitude]);

        centerGraphic.current = new Feature();
        centerGraphic.current.setStyle(centerGraphicStyle);
        centerGraphic.current.setGeometry(centerPoint);

        outlineGraphic.current = new Feature();
        outlineGraphic.current.setStyle(outlineGraphicStyle);
        outlineGraphic.current.setGeometry(centerPoint);

        if (!pointLayer.current) {
            pointLayer.current = new VectorLayer({
                //@ts-ignore
                map: map,
                source: new VectorSource({
                    features: [centerGraphic.current, outlineGraphic.current]
                })
            });
        }
    };

    const mapGoTo = async (zoom) => {
        //@ts-ignore
        await map.getView().fit(centerGraphic.current.getGeometry(), { duration: 1000, maxZoom: zoom || 15 });
    };

    const onClick = () => {
        let nextState = locationState;
        switch (locationState) {
            case LocationState.Inactive:
                nextState = LocationState.Waiting;
                break;
            case LocationState.Watching:
                nextState = LocationState.Tracking;
                break;
            default:
                nextState = LocationState.Inactive;
                break;
        }
        setLocationState(nextState);
    };

    return (
        <>
            <div className="location-button" onClick={onClick} title={"Atrast manu novietojumu"}>
                <span
                    className={classnames({
                        "icon-geo-locate":
                            locationState === LocationState.Inactive || locationState === LocationState.Watching,
                        "icon-geo-load rotating": locationState === LocationState.Waiting,
                        "icon-geo-locate-active": locationState === LocationState.Tracking
                    })}
                />
            </div>
        </>
    );
};

export class LocationButton extends Control {
    constructor() {
        const element = document.createElement("div");
        element.className = "timberflowform-map-button location-container ol-unselectable ol-control";
        super({
            element
        });
        ReactDOM.render(<GeoLocation this={this} />, element);
    }
}
